'use client';

import { Input } from '@app/app/_components/Input/Input';
import { useTranslations } from 'next-intl';
import { ViolationMap } from '@app/lib/form/contracts/ViolationMap';
import { Profile } from '@app/domain/model/Profile';
import { useCallback, ChangeEvent } from 'react';
import { SessionResponse } from '@app/domain/model/eshop/life/session/SessionResponse';
import { ReadOnlyField } from '@app/app/_components/ReadOnlyField/ReadOnlyField';
import { AuthState } from '@app/infrastructure/auth/model/AuthState';

interface Props {
    authState: Nullable<AuthState>;
    errors?: ViolationMap;
    sessionData: Nullable<SessionResponse>;
}

export function ContactDetailsStep({ authState, errors, sessionData }: Props) {
    const t = useTranslations();

    const extractPhoneNumber = useCallback((phone: Nullable<string>): string => {
        if (!phone) return '';
        return phone.replace(/^\+?372\s*/, '').trim();
    }, []);

    const handlePhoneChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        let value = e.target.value.replace(/\D/g, '');

        if (value.length > 8) {
            value = value.slice(0, 8);
        }

        e.target.value = value;
    }, []);

    const profile: Nullable<Profile> = authState?.profile ?? null;

    return (
        <div className="flex flex-col gap-6 max-sm:mb-20">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <Input
                    name="firstName"
                    label={t('profile.firstNameLabel')}
                    placeholder={t('profile.firstNameLabel')}
                    defaultValue={
                        sessionData?.secondStep?.policyHolder?.firstName ??
                        profile?.firstName ??
                        authState?.firstName ??
                        ''
                    }
                    error={errors?.firstName}
                />
                <Input
                    name="lastName"
                    label={t('profile.lastNameLabel')}
                    placeholder={t('profile.lastNameLabel')}
                    defaultValue={
                        sessionData?.secondStep?.policyHolder?.lastName ?? profile?.name ?? authState?.lastName ?? ''
                    }
                    error={errors?.lastName}
                />
            </div>

            <ReadOnlyField label={t('eshop.life.personalCode')}>
                {profile?.personalCode ?? authState?.personalCode}
            </ReadOnlyField>

            <Input
                name="email"
                type="email"
                label={t('eshop.life.emailAddress')}
                placeholder={t('profile.emailPlaceholder')}
                defaultValue={sessionData?.secondStep?.policyHolder?.email ?? profile?.mainEmail ?? ''}
                error={errors?.email}
            />

            <Input
                name="phoneNumber"
                type="tel"
                label={t('profile.mobilePhoneLabel')}
                placeholder={t('eshop.life.phonePlaceholder')}
                prefix="+372"
                prefixClassName="bg-neutral-50 text-neutral-500"
                defaultValue={
                    sessionData?.secondStep?.policyHolder?.phone
                        ? extractPhoneNumber(sessionData.secondStep.policyHolder.phone)
                        : extractPhoneNumber(profile?.mainPhone ?? '')
                }
                error={errors?.phoneNumber}
                onChange={handlePhoneChange}
                noSpaces
            />
        </div>
    );
}
